package com.snakesworld.game.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.snakesworld.game.audio.SoundManager
import com.snakesworld.game.domain.ElderDetermination
import com.snakesworld.game.domain.PelletSpawnCalculator
import com.snakesworld.game.engine.Direction
import com.snakesworld.game.engine.GameConstants
import com.snakesworld.game.engine.GameEngine
import com.snakesworld.game.engine.GameState
import com.snakesworld.game.engine.Vector2D
import com.snakesworld.game.network.ConnectionStatus
import com.snakesworld.game.network.MultiplayerRepository
import com.snakesworld.game.network.SnakeNetworkState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import kotlin.math.log10
import kotlin.time.Duration.Companion.milliseconds

data class GameUiState(
    val gameState: GameState = GameState(),
    val cameraZoom: Float = 1.0f,
    val cameraX: Float = 0f,
    val cameraY: Float = 0f,
    val connectionStatus: ConnectionStatus = ConnectionStatus.Connected,
    val isExitDialogVisible: Boolean = false,
    val userZoomMultiplier: Float = 1.0f,
    val isPaused: Boolean = false,
)

class GameViewModel(
    private val repository: MultiplayerRepository,
    private val soundManager: SoundManager,
    private val actionScope: CoroutineScope? = null,
) : ViewModel() {

    private val scope: CoroutineScope
        get() = actionScope ?: viewModelScope

    private val _uiState = MutableStateFlow(GameUiState())
    val uiState: StateFlow<GameUiState> = _uiState.asStateFlow()

    private var engine: GameEngine? = null
    private var lastBroadcastTs: Long = 0L
    private var lastBroadcastDirection: Direction = Direction.RIGHT
    private var lastBroadcastPosition: Vector2D? = null
    private var hasReceivedPelletsSnapshot = false
    private var userZoomMultiplier: Float = 1.0f

    private var networkObservationJob: Job? = null
    private var elderMaintenanceJob: Job? = null
    private var isInGameScreen: Boolean = false
    private var isSinglePlayerSession: Boolean = false
    private var persistStatsJob: Job? = null

    fun startGame(
        assignedUserId: String,
        nickname: String,
        skinId: String,
        startPosition: Vector2D? = null,
        isSinglePlayer: Boolean = false,
    ) {
        val currentEngine = engine
        if (currentEngine != null &&
            currentEngine.localPlayerId == assignedUserId &&
            isInGameScreen &&
            currentEngine.isSinglePlayer == isSinglePlayer
        ) {
            // Retain active match state across configuration changes and recompositions
            return
        }
        isInGameScreen = true
        isSinglePlayerSession = isSinglePlayer
        isCurrentlyBoosting = false
        userZoomMultiplier = 1.0f
        _uiState.value = _uiState.value.copy(
            userZoomMultiplier = 1.0f,
            connectionStatus = if (isSinglePlayer) ConnectionStatus.Connected else _uiState.value.connectionStatus,
            isPaused = false,
        )
        val newEngine = GameEngine(
            localPlayerId = assignedUserId,
            localPlayerName = nickname,
            skinId = skinId
        )
        newEngine.setSinglePlayerMode(isSinglePlayer)
        newEngine.initMatch(startPosition)
        if (!isSinglePlayer && repository.pellets.value.isNotEmpty()) {
            newEngine.updatePelletsFromNetwork(repository.pellets.value)
        }
        engine = newEngine

        soundManager.playStartFanfare()
        scope.launch {
            repository.recordGameStarted(isSinglePlayer = isSinglePlayer)
        }

        networkObservationJob?.cancel()
        elderMaintenanceJob?.cancel()
        if (isSinglePlayer) {
            return
        }

        // Observe network state
        networkObservationJob = viewModelScope.launch {
            launch {
                repository.connectionStatus.collect { status ->
                    _uiState.value = _uiState.value.copy(connectionStatus = status)
                }
            }
            launch {
                repository.snakes.collect { netSnakes ->
                    engine?.updateRemoteSnakesFromNetwork(netSnakes)
                }
            }
            launch {
                repository.pellets.collect { netPellets ->
                    hasReceivedPelletsSnapshot = true
                    engine?.updatePelletsFromNetwork(netPellets)
                }
            }
        }

        startElderMaintenanceLoop()
    }

    private fun startElderMaintenanceLoop() {
        elderMaintenanceJob?.cancel()
        elderMaintenanceJob = viewModelScope.launch {
            delay(3000.milliseconds)
            while (isActive && isInGameScreen) {
                val eng = engine
                if (eng == null || eng.isGameOver || !eng.localSnake.isAlive) {
                    break
                }
                performElderMaintenance()
                delay(3000.milliseconds)
            }
        }
    }

    fun onFrame(deltaTime: Float, currentTimestampNanos: Long) {
        val eng = engine ?: return
        val currentMs = currentTimestampNanos / 1_000_000L

        if (isSinglePlayerSession && (_uiState.value.isPaused || _uiState.value.isExitDialogVisible)) {
            return
        }

        val result = eng.tick(deltaTime, currentMs)

        // SFX Triggers
        if (result.atePellet) {
            soundManager.playPelletChime()
        }
        if (result.justDied) {
            isCurrentlyBoosting = false
            soundManager.stopBoost()
            soundManager.playDemise()
            elderMaintenanceJob?.cancel()

            val finalScore = eng.localSnake.score
            val finalKills = eng.localSnake.kills
            val finalSkinId = eng.localSnake.skinId
            val finalLength = eng.localSnake.segments.size + 1
            val victimId = eng.localPlayerId

            // Persist personal results (single-player and multiplayer). Arena cleanup is multiplayer-only.
            persistStatsJob = scope.launch {
                if (!isSinglePlayerSession) {
                    val killerId = result.killerPlayerId
                    val drops = if (killerId.isNullOrBlank()) {
                        result.deathDrops
                    } else {
                        result.deathDrops.map { it.copy(kb = killerId) }
                    }
                    repository.submitDemise(victimId, drops)
                }
                repository.submitHighScore(
                    score = finalScore,
                    kills = finalKills,
                    skinId = finalSkinId,
                    length = finalLength,
                    isSinglePlayer = isSinglePlayerSession,
                )
            }
        }

        if (!isSinglePlayerSession) {
            // Submit consumed pellets
            if (result.consumedPelletIds.isNotEmpty()) {
                scope.launch {
                    repository.consumePellets(result.consumedPelletIds)
                }
            }

            // Submit boost trail pellets
            if (result.spawnedTrailPellets.isNotEmpty()) {
                scope.launch {
                    repository.spawnPellets(result.spawnedTrailPellets)
                }
            }

            // Throttled Head State Broadcast (on step/turn, or heartbeat)
            if (eng.localSnake.isAlive) {
                val directionChanged = eng.localSnake.direction != lastBroadcastDirection
                val positionChanged = eng.localSnake.headPosition != lastBroadcastPosition
                val elapsedSinceLast = currentMs - lastBroadcastTs
                val shouldBroadcast = (positionChanged && elapsedSinceLast > 100L) ||
                        (directionChanged && elapsedSinceLast > 100L) ||
                        (elapsedSinceLast > 2000L)

                if (shouldBroadcast) {
                    lastBroadcastTs = currentMs
                    lastBroadcastDirection = eng.localSnake.direction
                    lastBroadcastPosition = eng.localSnake.headPosition
                    val headState = SnakeNetworkState(
                        id = eng.localPlayerId,
                        x = (eng.localSnake.headPosition.x * 10f).toInt() / 10f,
                        y = (eng.localSnake.headPosition.y * 10f).toInt() / 10f,
                        a = eng.localSnake.direction.angle,
                        s = eng.localSnake.speed,
                        b = eng.localSnake.isBoosting,
                        m = eng.localSnake.mass,
                        n = eng.localSnake.name,
                        sk = eng.localSnake.skinId,
                        ts = currentMs,
                    )
                    scope.launch {
                        repository.broadcastHeadState(headState)
                    }
                }
            }
        }

        // Compute adaptive camera zoom & 60 FPS smooth camera position tracking
        val baseTargetZoom = (1.0f - log10(eng.localSnake.mass.coerceAtLeast(5f) / 5f) * 0.20f).coerceIn(0.45f, 1.15f)
        val targetZoom = (baseTargetZoom * userZoomMultiplier).coerceIn(0.20f, 2.5f)
        val smoothedZoom = _uiState.value.cameraZoom + (targetZoom - _uiState.value.cameraZoom) * (deltaTime * 4.0f).coerceIn(0f, 1f)

        val targetCamX = eng.localSnake.headPosition.x
        val targetCamY = eng.localSnake.headPosition.y
        val prevCamX = _uiState.value.cameraX
        val prevCamY = _uiState.value.cameraY
        val isFirstCamFrame = (prevCamX == 0f && prevCamY == 0f && (targetCamX != 0f || targetCamY != 0f))
        val smoothedCamX = if (isFirstCamFrame) targetCamX else prevCamX + (targetCamX - prevCamX) * (deltaTime * 8.0f).coerceIn(0f, 1f)
        val smoothedCamY = if (isFirstCamFrame) targetCamY else prevCamY + (targetCamY - prevCamY) * (deltaTime * 8.0f).coerceIn(0f, 1f)

        _uiState.value = _uiState.value.copy(
            gameState = eng.getSnapshot(),
            cameraZoom = smoothedZoom,
            cameraX = smoothedCamX,
            cameraY = smoothedCamY,
            isPaused = eng.isPaused,
        )
    }

    fun togglePause(timestampMs: Long = 0L) {
        if (!isSinglePlayerSession) return
        val eng = engine ?: return
        if (eng.isGameOver || !eng.localSnake.isAlive) return
        if (_uiState.value.isPaused) {
            resumeGame(timestampMs)
        } else {
            pauseGame(timestampMs)
        }
    }

    fun pauseGame(timestampMs: Long = 0L) {
        if (!isSinglePlayerSession) return
        val eng = engine ?: return
        if (eng.isGameOver || !eng.localSnake.isAlive) return
        if (_uiState.value.isPaused) return
        isCurrentlyBoosting = false
        soundManager.stopBoost()
        eng.pause(timestampMs)
        _uiState.value = _uiState.value.copy(isPaused = true)
    }

    fun resumeGame(timestampMs: Long = 0L) {
        if (!_uiState.value.isPaused) return
        val eng = engine ?: return
        eng.resume(timestampMs)
        _uiState.value = _uiState.value.copy(isPaused = false)
    }

    fun onZoomIn() {
        userZoomMultiplier = (userZoomMultiplier * 1.15f).coerceIn(0.35f, 2.5f)
        _uiState.value = _uiState.value.copy(userZoomMultiplier = userZoomMultiplier)
    }

    fun onZoomOut() {
        userZoomMultiplier = (userZoomMultiplier / 1.15f).coerceIn(0.35f, 2.5f)
        _uiState.value = _uiState.value.copy(userZoomMultiplier = userZoomMultiplier)
    }

    fun onZoomChange(factor: Float) {
        userZoomMultiplier = (userZoomMultiplier * factor).coerceIn(0.35f, 2.5f)
        _uiState.value = _uiState.value.copy(userZoomMultiplier = userZoomMultiplier)
    }

    fun onResetZoom() {
        userZoomMultiplier = 1.0f
        _uiState.value = _uiState.value.copy(userZoomMultiplier = 1.0f)
    }

    fun onDirectionInput(direction: Direction) {
        if (isSinglePlayerSession && _uiState.value.isPaused) return
        soundManager.playDirectionClick()
        engine?.setQueuedDirection(direction)
    }

    private var isCurrentlyBoosting: Boolean = false

    fun onBoostInput(isBoosting: Boolean) {
        if (isSinglePlayerSession && _uiState.value.isPaused) return
        if (isCurrentlyBoosting == isBoosting) return
        isCurrentlyBoosting = isBoosting
        engine?.setBoosting(isBoosting)
        if (isBoosting) {
            soundManager.startBoost()
        } else {
            soundManager.stopBoost()
        }
    }

    fun showExitDialog(visible: Boolean) {
        _uiState.value = _uiState.value.copy(isExitDialogVisible = visible)
        if (isSinglePlayerSession) {
            val eng = engine
            if (visible) {
                isCurrentlyBoosting = false
                soundManager.stopBoost()
                eng?.pause()
            } else if (!_uiState.value.isPaused) {
                eng?.resume()
            }
        }
    }

    fun onConfirmExit(onExitCompleted: () -> Unit) {
        showExitDialog(false)
        _uiState.value = _uiState.value.copy(isPaused = false)
        isCurrentlyBoosting = false
        soundManager.stopBoost()
        isInGameScreen = false
        elderMaintenanceJob?.cancel()
        networkObservationJob?.cancel()
        val eng = engine
        engine = null
        val singlePlayer = isSinglePlayerSession

        suspend fun persistOnExit() {
            eng?.let {
                val alreadyOver = it.isGameOver || !it.localSnake.isAlive
                if (!alreadyOver) {
                    repository.submitHighScore(
                        score = it.localSnake.score,
                        kills = it.localSnake.kills,
                        skinId = it.localSnake.skinId,
                        length = it.localSnake.segments.size + 1,
                        isSinglePlayer = singlePlayer,
                    )
                } else {
                    // Death path may still be writing; wait so the SP menu reload sees fresh data.
                    persistStatsJob?.join()
                }
            }
        }

        if (singlePlayer) {
            // Await stats write before navigating so Single Player menu loadProfile is fresh.
            scope.launch {
                try {
                    withTimeoutOrNull(2500L.milliseconds) { persistOnExit() }
                } catch (_: Throwable) {}
                onExitCompleted()
            }
        } else {
            // Navigate immediately; leaveArena / late score write stay in background.
            onExitCompleted()
            CoroutineScope(Dispatchers.Default).launch {
                try {
                    withTimeoutOrNull(2000L.milliseconds) { persistOnExit() }
                } catch (_: Throwable) {}
                try {
                    withTimeoutOrNull(2000L.milliseconds) { repository.leaveArena() }
                } catch (_: Throwable) {}
            }
        }
    }

    fun onPlayAgain() {
        val eng = engine ?: return
        eng.initMatch()
        lastBroadcastPosition = null
        _uiState.value = _uiState.value.copy(
            cameraX = eng.localSnake.headPosition.x,
            cameraY = eng.localSnake.headPosition.y,
            isPaused = false,
        )
        if (!isSinglePlayerSession && repository.pellets.value.isNotEmpty()) {
            eng.updatePelletsFromNetwork(repository.pellets.value)
        }
        soundManager.playStartFanfare()
        scope.launch {
            repository.recordGameStarted(isSinglePlayer = isSinglePlayerSession)
        }
        if (!isSinglePlayerSession) {
            startElderMaintenanceLoop()
        }
    }

    private fun performElderMaintenance() {
        if (!isInGameScreen) return
        val eng = engine ?: return
        if (eng.isGameOver || !eng.localSnake.isAlive) return
        if (!hasReceivedPelletsSnapshot && repository.pellets.value.isEmpty()) return

        val allSnakes = repository.snakes.value.values
        val isElder = ElderDetermination.isElder(eng.localPlayerId, allSnakes)
        if (!isElder) return

        val now = (lastBroadcastTs).coerceAtLeast(1000L)
        // 1. Prune stale zombie snakes
        val staleIds = ElderDetermination.findStaleSnakes(allSnakes, now)
        for (staleId in staleIds) {
            if (staleId != eng.localPlayerId) {
                val orbs = eng.corpseOrbsFor(staleId)
                viewModelScope.launch {
                    repository.submitDemise(staleId, orbs)
                }
            }
        }

        // 2. Replenish ambient pellets if below target and below max cap.
        // Pellets are NEVER removed or cleared by maintenance; they are ONLY removed when eaten by a snake.
        val currentPellets = repository.pellets.value
        val ambientPellets = currentPellets.values.filter { !it.cp && !it.bt }
        val targetCount = PelletSpawnCalculator.calculateTargetPelletCount(allSnakes.size)
        val maxCap = GameConstants.MAX_PELLET_COUNT

        // Do not add more if max number or target is reached!
        if (ambientPellets.size < targetCount && ambientPellets.size < maxCap) {
            val missing = minOf(targetCount - ambientPellets.size, maxCap - ambientPellets.size)
            // Only spawn if a notable deficit exists (at least 10 missing) to avoid micro-thrashing
            if (missing >= 10) {
                val batch = PelletSpawnCalculator.generateAmbientPellets(count = minOf(missing, 15))
                viewModelScope.launch {
                    repository.spawnPellets(batch)
                }
            }
        }
    }

    override fun onCleared() {
        super.onCleared()
        isInGameScreen = false
        soundManager.stopBoost()
        networkObservationJob?.cancel()
        elderMaintenanceJob?.cancel()
    }
}
